"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

/** Copies `text`; says "Copied" for a moment. Falls back to selecting nothing silently when the clipboard is blocked. */
export function CopyButton({
  text,
  label = "Copy",
  className = "",
  onCopied,
}: {
  text: string;
  label?: string;
  className?: string;
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setFailed(false);
      onCopied?.();
    } catch {
      setFailed(true);
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCopied(false);
      setFailed(false);
    }, 1800);
  }

  return (
    <button
      type="button"
      onClick={copy}
      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-[#2a475e] bg-[#16202d] px-2.5 py-1.5 text-xs font-medium text-[#c7d5e0] transition-colors hover:border-[#3a556e] hover:text-white ${className}`}
      aria-live="polite"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-[#a4d007]" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      {copied ? "Copied" : failed ? "Select & copy" : label}
    </button>
  );
}

/** A dark code block with a copy button. */
export function CodeBlock({ code, label, className = "" }: { code: string; label?: string; className?: string }) {
  return (
    <div className={`relative rounded-lg border border-[#2a475e]/70 bg-[#0b121a] ${className}`}>
      {label && (
        <div className="flex items-center justify-between gap-2 border-b border-[#2a475e]/60 px-3 py-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[#8f98a0]">{label}</span>
          <CopyButton text={code} />
        </div>
      )}
      {/* Without a label the copy button floats over the code's top-right corner; the padding lets a long line scroll out from under it. */}
      <pre className={`overflow-x-auto px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-[#c7d5e0] ${label ? "" : "pr-24"}`}>
        <code>{code}</code>
      </pre>
      {!label && <CopyButton text={code} className="absolute right-2 top-2" />}
    </div>
  );
}
